import { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  SvgIcon,
  CircularProgress,
  styled,
  InputBase,
  Select,
  MenuItem,
  Chip,
  IconButton
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import ContentCard from './ContentCard';
import {
  colorLinkGrey,
  colorTextBlack,
  colorTextGrey,
  colorSecondary
} from '../../config/theme';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  getAllContentBySection,
  getLibraryListAll,
  revertResources,
  getContentTags
} from '../../redux/reducers/resourcesSlice';
import { useSelector } from 'react-redux';
import { ChevronRightIcon } from '../../utils/iconSvg';
import SnackBarBox from '../../components/SnackBarBox';
import { handleError, isArrayNotEmpty } from '../../utils/utils';
import _ from 'lodash';
import { getGamificationPoints } from '../../redux/reducers/layoutSlice';
import {
  getNoAuthGradeList,
  getThemeList,
  getMediumList,
  getUnitsByGrade
} from '../../redux/reducers/mastersSlice';
import { ADMIN_ROLE_TYPE } from '../../utils/constant';
import {
  ALLOWED_CONTENT_TYPE_OPTIONS,
  mapContentTypeToApi
} from '../../utils/navigationMenu';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,

  marginLeft: 0,
  // marginRight: 10,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto'
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  // padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  right: 6,
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

let searchValue = '';
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  height: '2rem',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 1.5),
    paddingRight: `calc(1em + ${theme.spacing(2)})`,
    transition: theme.transitions.create('width'),
    width: searchValue ? '28ch' : '0px',
    border: searchValue ? '1px solid #CBD2D9' : '',
    borderRadius: searchValue ? '8px' : '',
    [theme.breakpoints.up('sm')]: {
      '&:focus': {
        width: searchValue ? '28ch' : '28ch',
        border: '1px solid #CBD2D9',
        borderRadius: '8px'
      }
    }
  }
}));

const ResourcewiseContent = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    isSectionWiseContentSuccess,
    sectionWiseContentList,
    isSectionWiseContentLoading,
    isSaveToWatchLaterLoading,
    isSaveToWatchLaterSuccess,
    isSaveToWatchLaterFailed,
    saveToWatchLaterResponse,
    saveToWatchLaterErrorContainer,
    isUpdateLikeDislikeLoading,
    isUpdateLikeDislikeSuccess,
    updateLikeDislikeResponse,
    libraryListAll,
    isGetLibraryListAllSuccess,
    isGetLibraryListAllLoading,
    isGamificationLoading,
    isGamificationSuccess,
    isDeleteGamificationLoading,
    isDeleteGamificationSuccess,
    isGetContentTagsSuccess,
    contentTagList
  } = useSelector(state => state.resource);

  const { authData } = useSelector(state => state.auth);
  const { noAuthGradeList, themeList, mediumList, unitList } = useSelector(
    state => state.masters
  );

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = _.debounce(term => setSearchTerm(term), 300);
  const [contentData, setContentData] = useState([]);
  const [changeKey, setChangeKey] = useState(false);

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(20);

  // Filter states
  const [grade, setGrade] = useState();
  const [placeholder, setPlaceholder] = useState('Select grade');
  const [tags, setTags] = useState([]);
  const [theme, setTheme] = useState();
  const [themePlaceholder, setThemePlaceholder] = useState('Select theme');
  const [medium, setMedium] = useState();
  const [mediumPlaceholder, setMediumPlaceholder] = useState('Select medium');
  const [unit, setUnit] = useState();
  const [unitPlaceholder, setUnitPlaceholder] = useState('Select unit');
  const [contentType, setContentType] = useState(
    location.state?.contentType || ''
  );
  const [contentTypePlaceholder, setContentTypePlaceholder] = useState(
    'Select content type'
  );
  const [contentTags, setContentTags] = useState([]);

  // Check if user is admin
  const isAdmin = authData?.roleType === ADMIN_ROLE_TYPE;

  // Filter grades for non-admin users (only grades 1-10); State content is grades 1-10 only
  const filteredGradeList = useMemo(() => {
    const restrictToPrimaryGrades = list =>
      (list || []).filter(
        gradeItem => gradeItem.gradeId >= 1 && gradeItem.gradeId <= 10
      );

    if (contentType === 'State') {
      return restrictToPrimaryGrades(noAuthGradeList);
    }
    if (isAdmin) {
      return noAuthGradeList;
    }
    return restrictToPrimaryGrades(noAuthGradeList);
  }, [noAuthGradeList, isAdmin, contentType]);

  const apiContentType = useMemo(
    () => (contentType ? mapContentTypeToApi(contentType) : ''),
    [contentType]
  );

  useEffect(() => {
    if (isGetContentTagsSuccess && contentTagList) {
      let result = contentTagList?.payload?.dataList || [];
      setContentTags(result);
    }
  }, [contentTagList, isGetContentTagsSuccess]);

  useEffect(() => {
    dispatch(getNoAuthGradeList());
    dispatch(getThemeList()); // Load themes for all users
    dispatch(getMediumList()); // Load medium for all users
    dispatch(getContentTags());
  }, []);

  useEffect(() => {
    setHasMore(true);
    if (location.pathname == '/content-space/home/all') {
      if (!isSectionWiseContentLoading && isSectionWiseContentSuccess) {
        if (sectionWiseContentList?.payload?.dataList.length === 0) {
          setHasMore(false);
        }
        if (!changeKey) {
          if (
            contentData.length +
              sectionWiseContentList?.payload?.dataList.length >=
            sectionWiseContentList?.payload?.totalCount
          ) {
            setHasMore(false);
          }
        }

        if (page === 0 || changeKey) {
          setChangeKey(false);
          setContentData(sectionWiseContentList?.payload?.dataList || []);
        } else {
          setContentData(prev => [
            ...prev,
            ...sectionWiseContentList.payload.dataList
          ]);
        }
      }
    } else {
      if (!isGetLibraryListAllLoading && isGetLibraryListAllSuccess) {
        setContentData(libraryListAll.contentList);

        if (libraryListAll?.contentList?.length === 0) {
          setHasMore(false);
        }
        if (
          contentData.length + libraryListAll.contentList.length >=
          libraryListAll?.totalCount
        ) {
          setHasMore(false);
        }
        if (page === 0) {
          setContentData(libraryListAll?.contentList || []);
        } else {
          setContentData(prev => [...prev, ...libraryListAll.contentList]);
        }
      }
    }
    setLoading(false);
  }, [
    isSectionWiseContentLoading,
    isSectionWiseContentSuccess,
    isGetLibraryListAllLoading,
    isGetLibraryListAllSuccess
  ]);

  useEffect(() => {
    dispatch(revertResources());
    if (
      location?.state?.sectionId &&
      location.pathname == '/content-space/home/all'
    ) {
      let payload = {
        page: page,
        size: pageSize,
        type: 'all'
      };
      let queryParam = {
        search: searchTerm || '',
        grade: grade || '',
        sectionId: location?.state?.sectionId || '',
        userId: authData?.userId,
        encryptedContentId: '',
        tags: Array.isArray(tags) ? tags.join(',') : '',
        themeId: theme || '',
        mediumId: medium || '',
        unitId: unit || '',
        contentType: apiContentType
      };
      dispatch(getAllContentBySection({ payload, queryParam }));
    } else if (
      location?.state?.sectionId &&
      location.pathname == '/content-space/library/all'
    ) {
      const payload = {
        userId: authData?.userId,
        contentType: 'All',
        startDate: '',
        endDate: '',
        dateType: 'all',
        section: location?.state?.sectionId,
        paginationRequest: {
          type: 'all',
          page: page,
          size: pageSize
        }
      };
      const search = searchTerm;
      dispatch(getLibraryListAll({ payload, search }));
    }
  }, [
    location.state.sectionId,
    searchTerm,
    page,
    grade,
    theme,
    medium,
    unit,
    tags,
    apiContentType
  ]);

  useEffect(() => {
    if (isUpdateLikeDislikeSuccess && updateLikeDislikeResponse) {
      dispatch(getGamificationPoints(authData?.userId || 0));
      if (location.pathname === '/content-space/home/all') {
        setChangeKey(true);
        window.scrollTo({
          top: 0,
          behavior: 'smooth' // Optional: makes the scrolling smooth
        });
        setPage(0);
        let payload = {
          page: 0,
          size: pageSize,
          type: 'all'
        };
        let queryParam = {
          search: searchTerm || '',
          grade: grade || '',
          sectionId: location?.state?.sectionId,
          userId: authData?.userId,
          encryptedContentId: '',
          tags: Array.isArray(tags) ? tags.join(',') : '',
          themeId: theme || '',
          mediumId: medium || '',
          unitId: unit || '',
          contentType: apiContentType
        };
        dispatch(getAllContentBySection({ payload, queryParam }));
      } else {
        setChangeKey(true);
        window.scrollTo({
          top: 0,
          behavior: 'smooth' // Optional: makes the scrolling smooth
        });
        setPage(0);

        const payload = {
          userId: authData?.userId,
          contentType: 'All',
          startDate: '',
          endDate: '',
          dateType: 'all',
          section: location?.state?.sectionId,
          paginationRequest: {
            type: 'all',
            page: 0,
            size: 20
          }
        };
        const search = searchTerm;
        dispatch(getLibraryListAll({ payload, search }));
      }
    }
  }, [isUpdateLikeDislikeSuccess, isUpdateLikeDislikeLoading]);

  const handleTags = data => {
    setTags(data);
    setPage(0);
    setContentData([]);
    setChangeKey(true);
  };

  const handleSearch = e => {
    const term = e.target.value;
    if (term.length >= 3 || term.length === 0) {
      debouncedSearch(term);
      setPage(0);
      setContentData([]);
    }
  };

  useEffect(() => {
    let timeoutId = null;

    const handleScroll = () => {
      if (!loading && hasMore) {
        const nearBottom =
          window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.scrollHeight - 100;

        if (nearBottom) {
          console.log('Bottom');
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            setPage(prevPage => {
              const maxPage =
                Math.ceil(
                  location.pathname == '/content-space/home/all'
                    ? sectionWiseContentList?.payload?.totalCount
                    : libraryListAll?.totalCount / pageSize
                ) - 1;
              return prevPage < maxPage ? prevPage + 1 : prevPage;
            });
          }, 200);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [loading, hasMore, sectionWiseContentList, libraryListAll]);

  useEffect(() => {
    if (!isGamificationLoading && isGamificationSuccess) {
      dispatch(getGamificationPoints(authData?.userId || 0));
    }
    if (!isDeleteGamificationLoading && isDeleteGamificationSuccess) {
      dispatch(getGamificationPoints(authData?.userId || 0));
    }
  }, [
    isGamificationLoading,
    isGamificationSuccess,
    isDeleteGamificationLoading,
    isDeleteGamificationSuccess
  ]);

  return (
    <>
      {/* Tags Filter Bar */}
      <TagsBar tags={contentTags} handleTags={handleTags} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '12px',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex' }}>
          <Typography
            style={{
              fontSize: '24px',
              fontFamily: 'Lato',
              lineHeight: '32px',
              fontWeight: 700,
              color: colorLinkGrey,
              cursor: 'pointer'
            }}
            onClick={() => {
              if (location.pathname == '/content-space/home/all') {
                navigate('/content-space/home');
              } else if (location.pathname == '/content-space/library/all') {
                navigate('/content-space/library');
              }
            }}
          >
            {location.pathname == '/content-space/home/all'
              ? 'Home'
              : location.pathname == '/content-space/library/all'
                ? 'Library'
                : ''}
          </Typography>
          <SvgIcon sx={{ height: 32, width: 32 }}>
            <ChevronRightIcon color={colorTextGrey} />
          </SvgIcon>
          <Typography
            style={{
              fontSize: '32px',
              fontFamily: 'Lato',
              lineHeight: '36px',
              fontWeight: 700,
              color: colorTextBlack
            }}
          >
            {location?.state?.sectionName || ''}
          </Typography>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: '700',
              color: colorTextBlack,
              marginLeft: '10px',
              marginTop: '5px'
            }}
          >
            {`(${location.state?.subTitle})`}
          </Typography>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Search>
            <SearchIconWrapper>
              <svg
                width="21"
                height="20"
                viewBox="0 0 21 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 17.5L14.375 13.875M16.3333 9.16667C16.3333 12.8486 13.3486 15.8333 9.66667 15.8333C5.98477 15.8333 3 12.8486 3 9.16667C3 5.48477 5.98477 2.5 9.66667 2.5C13.3486 2.5 16.3333 5.48477 16.3333 9.16667Z"
                  stroke="#ABB4BD"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </SearchIconWrapper>
            <StyledInputBase
              onChange={e => {
                searchValue = e.target.value;
                handleSearch(e);
              }}
              placeholder="Search here…"
            />
          </Search>

          <Select
            value={contentType || contentTypePlaceholder}
            onChange={event => {
              const selectedValue = event.target.value;
              if (selectedValue === contentTypePlaceholder) {
                return;
              }
              if (selectedValue === '') {
                setContentType('');
              } else {
                setContentType(selectedValue);
                if (selectedValue === 'State') {
                  setTheme('');
                  if (grade && grade > 10) {
                    setGrade('');
                  }
                }
                if (selectedValue === 'Core') {
                  setUnit('');
                }
                setContentTypePlaceholder('Select content type');
              }
              setPage(0);
              setContentData([]);
              setChangeKey(true);
            }}
            name="contentType"
            displayEmpty
            sx={{
              textAlign: 'left',
              width: '160px',
              height: '42px',
              marginLeft: '10px'
            }}
          >
            <MenuItem disabled value="Select content type">
              <em>Select content type</em>
            </MenuItem>
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {ALLOWED_CONTENT_TYPE_OPTIONS.map(option => (
              <MenuItem
                key={option.id}
                value={option.id}
                sx={{
                  ':hover': {
                    backgroundColor: colorSecondary
                  }
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>

          {/* Grade Dropdown */}
          <Select
            value={grade || placeholder}
            onChange={event => {
              const selectedValue = event.target.value;
              if (selectedValue === '') {
                setGrade('');
                setTheme('');
                setUnit('');
                setPage(0);
                setContentData([]);
                setChangeKey(true);
              } else {
                setGrade(selectedValue);
                // Fetch units for non-admin users
                if (!isAdmin) {
                  dispatch(getUnitsByGrade(selectedValue));
                  setUnit(''); // Reset unit when grade changes
                }
                // Clear theme if grade is SENIOR (11) or JUNIOR (12)
                if (selectedValue > 10) {
                  setTheme('');
                }
                setPage(0);
                setContentData([]);
                setChangeKey(true);
                setPlaceholder('Select grade');
              }
            }}
            name="grade"
            sx={{
              textAlign: 'left',
              width: '160px',
              height: '42px',
              marginLeft: '10px'
            }}
          >
            <MenuItem disabled value="Select grade">
              <em>Select grade</em>
            </MenuItem>
            {isArrayNotEmpty(filteredGradeList) &&
              filteredGradeList.map((row, key) => {
                return (
                  <MenuItem
                    key={key}
                    value={row?.gradeId}
                    sx={{
                      ':hover': {
                        backgroundColor: colorSecondary
                      }
                    }}
                  >
                    {row?.gradeName}
                  </MenuItem>
                );
              })}
          </Select>

          {/* Theme dropdown - Only show for admin users with grades 1-10 */}
          {isAdmin && grade && grade <= 10 && contentType !== 'State' && (
            <Select
              value={theme || themePlaceholder}
              onChange={event => {
                const selectedValue = event.target.value;
                if (selectedValue === '') {
                  setTheme('');
                } else {
                  setTheme(selectedValue);
                  setThemePlaceholder('Select theme');
                }
                setPage(0);
                setContentData([]);
                setChangeKey(true);
              }}
              name="theme"
              sx={{
                textAlign: 'left',
                width: '160px',
                height: '42px',
                marginLeft: '10px'
              }}
            >
              <MenuItem disabled value="Select theme">
                <em>Select theme</em>
              </MenuItem>
              {isArrayNotEmpty(themeList) &&
                themeList.map((row, key) => {
                  return (
                    <MenuItem
                      key={key}
                      value={row?.themeId}
                      sx={{
                        ':hover': {
                          backgroundColor: colorSecondary
                        }
                      }}
                    >
                      {row?.themeName}
                    </MenuItem>
                  );
                })}
            </Select>
          )}

          {/* Medium dropdown - Show for all users */}
          <Select
            value={medium || mediumPlaceholder}
            onChange={event => {
              const selectedValue = event.target.value;
              if (selectedValue === '') {
                setMedium('');
              } else {
                setMedium(selectedValue);
                setMediumPlaceholder('Select medium');
              }
              setPage(0);
              setContentData([]);
              setChangeKey(true);
            }}
            name="medium"
            sx={{
              textAlign: 'left',
              width: '160px',
              height: '42px',
              marginLeft: '10px'
            }}
          >
            <MenuItem disabled value="Select medium">
              <em>Select medium</em>
            </MenuItem>
            {isArrayNotEmpty(mediumList) &&
              mediumList.map((row, key) => {
                return (
                  <MenuItem
                    key={key}
                    value={row?.mediumId}
                    sx={{
                      ':hover': {
                        backgroundColor: colorSecondary
                      }
                    }}
                  >
                    {row?.mediumName}
                  </MenuItem>
                );
              })}
          </Select>

          {/* Unit dropdown - Only for non-admin users and when grade is selected */}
          {!isAdmin && grade && contentType !== 'Core' && (
            <Select
              value={unit || unitPlaceholder}
              onChange={event => {
                const selectedValue = event.target.value;
                if (selectedValue === '') {
                  setUnit('');
                } else {
                  setUnit(selectedValue);
                  setUnitPlaceholder('Select unit');
                }
                setPage(0);
                setContentData([]);
                setChangeKey(true);
              }}
              name="unit"
              sx={{
                textAlign: 'left',
                width: '160px',
                height: '42px',
                marginLeft: '10px'
              }}
            >
              <MenuItem disabled value="Select unit">
                <em>Select unit</em>
              </MenuItem>
              {isArrayNotEmpty(unitList) &&
                unitList.map((row, key) => {
                  return (
                    <MenuItem
                      key={key}
                      value={row?.unitId}
                      sx={{
                        ':hover': {
                          backgroundColor: colorSecondary
                        }
                      }}
                    >
                      {row?.unitName}
                    </MenuItem>
                  );
                })}
            </Select>
          )}
        </div>
      </Box>

      {changeKey ? (
        <div
          style={{
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '70vh'
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <>
          {contentData?.length > 0 ? (
            <Box
              sx={{
                marginBottom: 4,
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '8px'
              }}
            >
              <Grid container spacing={2}>
                {contentData &&
                  contentData?.map((video, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <ContentCard
                        video={video}
                        sectionID={location?.state?.sectionId}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Box>
          ) : (
            !isSectionWiseContentLoading &&
            !isGetLibraryListAllLoading &&
            contentData.length == 0 && (
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  padding: '16px',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  style={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: colorTextGrey
                  }}
                >
                  {' '}
                  No content available
                </Typography>
              </Box>
            )
          )}
        </>
      )}

      {!isSaveToWatchLaterLoading &&
        isSaveToWatchLaterSuccess &&
        saveToWatchLaterResponse && (
          <SnackBarBox
            type="success"
            message={handleError(saveToWatchLaterResponse?.payload?.message)}
          />
        )}

      {!isSaveToWatchLaterLoading &&
        saveToWatchLaterErrorContainer &&
        isSaveToWatchLaterFailed && (
          <SnackBarBox
            type="error"
            message={handleError(saveToWatchLaterErrorContainer?.errorMessage)}
          />
        )}

      {hasMore && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress />
        </Box>
      )}

      {(sectionWiseContentList?.payload?.totalCount !== 0 ||
        libraryListAll?.totalCount !== 0) &&
        !hasMore && (
          <SnackBarBox
            type="success"
            message={handleError('All records have been loaded.')}
          />
        )}
    </>
  );
};

export const TagsBar = ({ tags, handleTags }) => {
  const [showArrows, setShowArrows] = useState(false);
  const [selectedTagList, setSelectedTagList] = useState([]);
  const containerRef = useRef(null);

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  useLayoutEffect(() => {
    if (containerRef.current) {
      setShowArrows(
        containerRef.current.scrollWidth > containerRef.current.clientWidth
      );
    }
  }, [tags]);

  const handleOnClickTag = tagId => {
    let tempTagList = [...selectedTagList];

    if (tempTagList.includes(tagId)) {
      tempTagList = tempTagList.filter(id => id !== tagId);
    } else {
      tempTagList.push(tagId);
    }
    setSelectedTagList(tempTagList);
    handleTags(tempTagList);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
      {showArrows && (
        <IconButton onClick={scrollLeft}>
          <ArrowBackIos />
        </IconButton>
      )}

      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          overflowX: 'hidden',
          padding: 0.3,
          flexGrow: 1,
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        {tags.map((tag, index) => (
          <Chip
            label={tag?.tagName}
            key={index}
            sx={{
              marginRight: 1,
              border: selectedTagList.includes(tag?.contentTagId)
                ? '2px solid #F4C24A'
                : '1px solid #F7D178',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Lato',
              backgroundColor: selectedTagList.includes(tag?.contentTagId)
                ? '#FCEBC5'
                : '#FEF8EC',
              '&:hover': {
                backgroundColor: '#FCEBC5',
                borderColor: '#F4C24A'
              }
            }}
            onClick={() => handleOnClickTag(tag?.contentTagId)}
            color="secondary"
          />
        ))}
      </Box>

      {showArrows && (
        <IconButton onClick={scrollRight}>
          <ArrowForwardIos />
        </IconButton>
      )}
    </Box>
  );
};

export default ResourcewiseContent;
